import React, { forwardRef } from 'react'

/**
 * Button primitives for the dashboard.
 *
 * Three variants, matched to the dashboard conventions:
 *   - <PrimaryButton>    brand-600 solid. Use for the main CTA on a page.
 *   - <OutlineButton>    subtle outlined. Use for secondary actions (Add, Import, etc.).
 *   - <IconButton>       icon-only square. Use inside rows / toolbars.
 *
 * All three share the same base (padding, rounded-lg, font, transition) so
 * pages stay visually consistent even when authors combine them.
 */

type BaseProps = {
  icon?: React.ReactNode
  loading?: boolean
  className?: string
}

type ButtonHTMLProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps>

const base = 'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed'

const spinner = (
  <span className="w-4 h-4 border-2 border-current/40 border-t-current rounded-full animate-spin-slow" aria-hidden />
)

export const PrimaryButton = forwardRef<HTMLButtonElement, BaseProps & ButtonHTMLProps>(
  ({ icon, loading, className = '', children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`${base} px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white shadow-sm ${className}`}
      {...rest}
    >
      {loading ? spinner : icon}
      {children}
    </button>
  )
)
PrimaryButton.displayName = 'PrimaryButton'

export const OutlineButton = forwardRef<HTMLButtonElement, BaseProps & ButtonHTMLProps>(
  ({ icon, loading, className = '', children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`${base} px-3 py-1.5 bg-surface-secondary hover:bg-surface-hover text-txt-primary border border-border ${className}`}
      {...rest}
    >
      {loading ? spinner : icon}
      {children}
    </button>
  )
)
OutlineButton.displayName = 'OutlineButton'

export type IconButtonVariant = 'default' | 'danger'

export const IconButton = forwardRef<
  HTMLButtonElement,
  BaseProps & ButtonHTMLProps & { variant?: IconButtonVariant }
>(({ icon, className = '', variant = 'default', children, ...rest }, ref) => {
  const variantCls =
    variant === 'danger'
      ? 'text-txt-tertiary hover:text-error hover:bg-error-light'
      : 'text-txt-tertiary hover:text-brand-600 hover:bg-surface-hover'
  return (
    <button
      ref={ref}
      className={`${base} p-2 ${variantCls} ${className}`}
      {...rest}
    >
      {icon || children}
    </button>
  )
})
IconButton.displayName = 'IconButton'
